import { Filter } from "@/shared/lib/filter/filter";
import { Button } from "@/shared/components/ui/button";
import { Check, ListFilter } from "lucide-react";
import t from "@/lang/lang";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shared/components/ui/command";
import { cn } from "@/shared/lib/utils";
import { useCustomersServiceObjectListQuery } from "@/services/backend/customers/api/service-object-api";
import { NullUUID } from "@/shared/lib/utilities/uuid";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { Spinner } from "@/shared/components/ui/spinner";

export interface ServiceObjectFilterProps {
  filter: Filter<string> | undefined;
  onFilterChange: (filter: Filter<string>) => void;
  customerIds?: string[];
  hidden?: boolean;
}

export function ServiceObjectFilter({
  filter,
  onFilterChange,
  customerIds,
  hidden = false,
}: ServiceObjectFilterProps) {
  // This allows us to accept undefined filters,
  // which is common in list requests. This makes
  // it nicer to call the component.
  const filterVal = filter ?? { active: false, values: [] };
  // simplifies some code below
  const currentValues = filterVal.values ?? [];

  const {
    data: list,
    isLoading,
    error,
  } = useCustomersServiceObjectListQuery({
    customer: {
      active: !!customerIds,
      values: customerIds ?? [NullUUID],
    },
  });

  if (hidden) {
    return null;
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm">
          <ListFilter />
          {t("Objekte")}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0" align="start">
        <Command>
          <CommandInput placeholder={t("Suchen")} />
          {isLoading && (
            <span className="flex w-full items-center justify-center gap-2 py-3 text-sm">
              <Spinner />
              {t("Lade Daten ...")}
            </span>
          )}
          {error && (
            <RTKQueryErrorAlert error={error} className="border-none" />
          )}
          {list && (
            <CommandList>
              <CommandEmpty className="py-3 text-center text-sm">
                {list.serviceObjects.length === 0
                  ? t("Keine Objekte")
                  : t("Nicht gefunden")}
              </CommandEmpty>
              <CommandGroup>
                {list.serviceObjects.map((serviceObject) => (
                  <CommandItem
                    key={serviceObject.id}
                    value={serviceObject.name}
                    onSelect={() => {
                      const values = currentValues.includes(serviceObject.id)
                        ? currentValues.filter((v) => v !== serviceObject.id)
                        : [...currentValues, serviceObject.id];
                      onFilterChange({
                        active: values.length > 0,
                        values,
                      });
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        currentValues.includes(serviceObject.id)
                          ? "opacity-100"
                          : "opacity-0",
                      )}
                    />
                    {serviceObject.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
